import { useState, useEffect } from "react";
import { Users, MessageSquare, CreditCard, CheckCircle, XCircle, Clock, Activity, RefreshCw } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import API from "@/lib/api";
import { toast } from "sonner";
import { ADMIN } from "@/constants/testIds";

function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div data-testid={ADMIN.statsCard} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-all">
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 rounded-xl ${color} flex items-center justify-center shadow-sm`}>
          <Icon size={18} className="text-white" />
        </div>
      </div>
      <div className="text-2xl font-bold text-slate-900 font-mono mb-1">{value}</div>
      <div className="text-xs text-slate-500">{label}</div>
      {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}

const TOOLTIP_STYLE = { background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 8, color: "#0F172A", fontSize: 12 };

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadStats(); }, []);

  const loadStats = async () => {
    try {
      const { data } = await API.get("/admin/stats");
      setStats(data);
    } catch {
      toast.error("Failed to load stats");
    } finally {
      setLoading(false);
    }
  };

  const smsChartData = stats ? [
    { name: "Total", value: stats.sms.total, fill: "#3B82F6" },
    { name: "Delivered", value: stats.sms.delivered, fill: "#10B981" },
    { name: "Failed", value: stats.sms.failed, fill: "#EF4444" },
    { name: "Pending", value: stats.sms.pending, fill: "#F59E0B" },
  ] : [];

  const customerChartData = stats ? [
    { name: "Active", value: stats.customers.active, fill: "#10B981" },
    { name: "Suspended", value: stats.customers.suspended, fill: "#F59E0B" },
    { name: "Terminated", value: stats.customers.terminated, fill: "#EF4444" },
  ] : [];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-28 bg-slate-200 rounded-xl animate-pulse" />)}
        </div>
      </div>
    );
  }

  return (
    <div data-testid={ADMIN.dashboard} className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">Admin Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Platform overview and key metrics</p>
        </div>
        <button onClick={loadStats} className="p-2 text-slate-500 hover:text-[#0c1e41] bg-slate-100 hover:bg-[#eef1f8] rounded-lg transition-colors">
          <RefreshCw size={16} />
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Total Customers" value={stats?.customers?.total ?? 0} sub={`${stats?.customers?.active ?? 0} active`} color="bg-[#0c1e41]" />
        <StatCard icon={MessageSquare} label="Total SMS Sent" value={(stats?.sms?.total ?? 0).toLocaleString()} sub={`${stats?.sms?.today ?? 0} today`} color="bg-emerald-600" />
        <StatCard icon={CheckCircle} label="Delivery Rate" value={`${stats?.sms?.delivery_rate ?? 0}%`} sub={`${stats?.sms?.delivered ?? 0} delivered`} color="bg-purple-600" />
        <StatCard icon={CreditCard} label="Pending Payments" value={stats?.finance?.pending_payments ?? 0} sub={`${stats?.pending_sender_ids ?? 0} sender IDs pending`} color="bg-amber-500" />
      </div>

      {/* Secondary stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "SMS Today", value: stats?.sms?.today ?? 0, icon: Activity, color: "text-[#0c1e41]", bg: "bg-[#eef1f8]" },
          { label: "Delivered", value: stats?.sms?.delivered ?? 0, icon: CheckCircle, color: "text-emerald-600", bg: "bg-emerald-50" },
          { label: "Failed", value: stats?.sms?.failed ?? 0, icon: XCircle, color: "text-red-600", bg: "bg-red-50" },
          { label: "Pending", value: stats?.sms?.pending ?? 0, icon: Clock, color: "text-amber-600", bg: "bg-amber-50" },
        ].map((s) => (
          <div key={s.label} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3 shadow-sm">
            <div className={`w-9 h-9 rounded-xl ${s.bg} flex items-center justify-center shrink-0`}>
              <s.icon size={18} className={s.color} />
            </div>
            <div>
              <div className="text-lg font-bold text-slate-900 font-mono">{s.value.toLocaleString()}</div>
              <div className="text-xs text-slate-500">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">SMS Status Breakdown</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={smsChartData} barSize={32}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="name" tick={{ fill: "#64748B", fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#64748B", fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {smsChartData.map((entry, index) => (
                  <Cell key={index} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">Customer Status</h3>
          {stats?.customers?.total === 0 ? (
            <div className="flex items-center justify-center h-48 text-slate-400 text-sm">No customers yet</div>
          ) : (
            <div className="flex items-center justify-between">
              <ResponsiveContainer width="55%" height={200}>
                <PieChart>
                  <Pie data={customerChartData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value">
                    {customerChartData.map((entry, index) => (
                      <Cell key={index} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-3 flex-1">
                {customerChartData.map((d) => (
                  <div key={d.name} className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: d.fill }} />
                    <span className="text-xs text-slate-600 flex-1">{d.name}</span>
                    <span className="text-xs font-mono font-bold text-slate-900">{d.value}</span>
                  </div>
                ))}
                <div className="flex items-center gap-2 pt-1 border-t border-slate-200">
                  <div className="w-2.5 h-2.5 rounded-full shrink-0 bg-slate-400" />
                  <span className="text-xs text-slate-600 flex-1">Total</span>
                  <span className="text-xs font-mono font-bold text-slate-900">{stats?.customers?.total}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Platform Status */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-900 mb-4">Platform Status</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: "Pending Sender IDs", value: stats?.pending_sender_ids ?? 0, color: "text-amber-700", bg: "bg-amber-50 border-amber-200" },
            { label: "Pending Payments", value: stats?.finance?.pending_payments ?? 0, color: "text-[#0c1e41]", bg: "bg-[#eef1f8] border-[#c0cfe8]" },
            { label: "Suspended Accounts", value: stats?.customers?.suspended ?? 0, color: "text-red-700", bg: "bg-red-50 border-red-200" },
            { label: "Credits Paid Out", value: stats?.finance?.total_revenue_credits ?? 0, color: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200" },
          ].map((item) => (
            <div key={item.label} className={`${item.bg} border rounded-xl p-4 text-center`}>
              <div className={`text-2xl font-bold font-mono ${item.color}`}>{item.value.toLocaleString()}</div>
              <div className="text-xs text-slate-500 mt-1">{item.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
